import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    LogOut,
    Bell,
    Sparkles,
    User,
    Search
} from 'lucide-react';
import ChatSection from '../dashboard/ChatSection';
import ResizableModal from '../dashboard/ResizableModal';
import { APP_DISPLAY_TITLE, APP_NAME, ASSISTANT_NAME } from '../../branding';
import { usePocAccess } from '../poc/PocAccess';

// POC Claims and Warranty Admin links are hidden for now; their routes still work by direct URL.
const SHOW_POC_LINKS = false;

export default function Navbar({ children, userName }) {
    const location = useLocation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isAdmin } = usePocAccess();

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    // `section` marks Phase 4B-UI areas whose sub-pages keep the link highlighted.
    const navLinks = [
        { name: 'Dashboard', href: '/' },
        { name: 'Claims', href: '/claim' },
        { name: 'Reports', href: '/reports' },
        { name: 'Smart Table', href: '/smarttable' },
        ...(SHOW_POC_LINKS ? [{ name: 'POC Claims', href: '/poc-claims', section: '/poc-claims' }] : []),
        ...(SHOW_POC_LINKS && isAdmin ? [{ name: 'Warranty Admin', href: '/admin/warranty-policies', section: '/admin/' }] : [])
    ];

    const isActive = (link) => (link.section ? location.pathname.startsWith(link.section) : location.pathname === link.href);

    return (
        <div className="flex flex-col min-h-screen bg-background font-sans">
            {/* Tier 1: Top Global Header */}
            <header className="bg-white border-b border-line h-16 flex items-center justify-between px-8 z-20 flex-shrink-0">
                <div className="flex items-center min-w-0 mr-4">
                    {/* Short name on smaller screens, where the full title would crowd the header. */}
                    <h2 className="text-primary text-base xl:text-lg font-bold uppercase tracking-widest truncate" title={APP_DISPLAY_TITLE}>
                        <span className="lg:hidden">{APP_NAME}</span>
                        <span className="hidden lg:inline">{APP_DISPLAY_TITLE}</span>
                    </h2>
                </div>

                <div className="flex items-center space-x-6">
                    {/* Search Bar */}
                    <div className="hidden md:flex relative">
                        <input 
                            type="text" 
                            placeholder="SEARCH..." 
                            className="bg-background text-primary placeholder-text-muted rounded-none pl-10 pr-4 py-2 text-[10px] uppercase tracking-widest focus:outline-none border border-line focus:border-accent transition-colors w-64"
                        />
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-3 w-3 text-text-muted" />
                    </div>

                    <button className="text-primary hover:text-accent transition-colors">
                        <Bell className="h-5 w-5" />
                    </button>
                    
                    <div className="flex items-center space-x-3 text-primary cursor-pointer hover:text-accent transition-colors">
                        <div className="w-8 h-8 rounded-none border border-line flex items-center justify-center bg-background">
                            <User className="h-4 w-4" />
                        </div>
                        <div className="hidden md:block text-left">
                            <p className="text-[10px] font-semibold uppercase tracking-widest">{userName}</p>
                        </div>
                    </div>

                    <button className="text-primary hover:text-accent transition-colors ml-4" title="Logout">
                        <LogOut className="h-5 w-5" />
                    </button>
                </div>
            </header>

            {/* Tier 2: Secondary Horizontal Ribbon */}
            <nav className="bg-primary w-full flex justify-center items-center py-4 px-8 z-10 flex-shrink-0">
                <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 md:gap-x-12">
                    {navLinks.map((link) => {
                        const active = isActive(link);
                        return (
                            <Link
                                key={link.name}
                                to={link.href}
                                className={`text-xs uppercase tracking-[0.15em] font-semibold transition-colors duration-200 pb-2 border-b-2 ${
                                    active 
                                        ? 'text-white border-accent' 
                                        : 'text-line-strong hover:text-white border-transparent'
                                }`}
                            >
                                {link.name}
                            </Link>
                        );
                    })}
                </div>
            </nav>

            {/* Main Content Wrapper */}
            <main className="flex-1 overflow-auto p-8 relative">
                {children}

                {/* Floating AI Button */}
                <motion.button
                    onClick={handleOpenModal}
                    animate={{ rotate: isModalOpen ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    className="fixed bottom-8 right-8 bg-primary hover:bg-accent text-white p-4 rounded-full border border-white shadow-lg focus:outline-none z-50 flex items-center justify-center"
                    title={`Ask the ${ASSISTANT_NAME}`}
                    aria-label={`Ask the ${ASSISTANT_NAME}`}
                >
                    <Sparkles className="h-6 w-6" />
                </motion.button>
            </main>

            {/* AI Modal */}
            <ResizableModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            >
               <ChatSection onClose={handleCloseModal}/>
            </ResizableModal>
        </div>
    );
}